import React, { useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Check, Loader2, Trash2, Star, Image as ImageIcon, Camera } from "lucide-react";
import { CameraCapture } from "./CameraCapture";

const MAX_PHOTOS = 4;

const resizeImage = (file, max = 1200) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new window.Image();
    img.onload = () => {
      const ratio = Math.min(max / img.width, max / img.height, 1);
      const w = Math.round(img.width * ratio), h = Math.round(img.height * ratio);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject; img.src = reader.result;
  };
  reader.onerror = reject; reader.readAsDataURL(file);
});

// Build initial photo list from the product's existing images (first = main).
const initPhotos = (product) => {
  const urls = [product.featured_image, ...(product.images || [])].filter(Boolean);
  return urls.slice(0, MAX_PHOTOS).map((u) => ({ existing: u, original: null, cleaned: null, use: "existing", status: "done" }));
};

export const ProductImagesModal = ({ product, onClose, onSaved }) => {
  const [photos, setPhotos] = useState(() => initPhotos(product));
  const [saving, setSaving] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const fileRef = useRef(null);

  const addFromDataUrl = async (dataUrl) => {
    if (photos.length >= MAX_PHOTOS) { toast.error(`Up to ${MAX_PHOTOS} photos`); return; }
    const idx = photos.length;
    setPhotos((prev) => [...prev, { existing: null, original: dataUrl, cleaned: null, use: "cleaned", status: "cleaning" }]);
    try {
      const r = await api.post("/pos/photo/clean", { image_base64: dataUrl });
      setPhotos((prev) => prev.map((ph, i) => i === idx ? { ...ph, cleaned: r.data.cleaned_base64, status: "done" } : ph));
    } catch {
      toast.message("Auto-cleanup unavailable — keeping the original.");
      setPhotos((prev) => prev.map((ph, i) => i === idx ? { ...ph, use: "original", status: "failed" } : ph));
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (photos.length >= MAX_PHOTOS) { toast.error(`Up to ${MAX_PHOTOS} photos`); return; }
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    let dataUrl;
    try { dataUrl = await resizeImage(f); } catch { toast.error("Could not read that image"); return; }
    await addFromDataUrl(dataUrl);
  };

  const setUse = (i, use) => setPhotos((prev) => prev.map((ph, k) => k === i ? { ...ph, use } : ph));
  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, k) => k !== i));
  const makeMain = (i) => setPhotos((prev) => {
    const next = [...prev];
    const [picked] = next.splice(i, 1);
    return [picked, ...next];
  });

  const srcOf = (ph) => {
    if (ph.existing) return mediaUrl(ph.existing);
    return ph.use === "cleaned" && ph.cleaned ? ph.cleaned : ph.original;
  };
  const payloadOf = (ph) => {
    if (ph.existing) return ph.existing;
    return ph.use === "cleaned" && ph.cleaned ? ph.cleaned : ph.original;
  };

  const save = async () => {
    if (!photos.length) { toast.error("Add at least one photo"); return; }
    if (photos.some((p) => p.status === "cleaning")) { toast.error("Still cleaning a photo — hold on"); return; }
    const images = photos.map(payloadOf).filter(Boolean);
    setSaving(true);
    try {
      await api.post("/pos/photo/save", { product_id: product.id, images });
      toast.success("Images updated");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[70] flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 mt-10 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="product-images-modal">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-heading text-xl text-brand-navy">Product images</div>
            <div className="text-sm text-slate-500">{product.product_name} · {product.sku}</div>
          </div>
          <button onClick={onClose} data-testid="product-images-close" className="text-slate-500 hover:text-brand-red"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-xs text-slate-500">
          Up to {MAX_PHOTOS} photos. The first photo is the <b>main image</b>. New uploads are auto-cleaned
          (background removed, centered on white) — accept the cleaned version or keep your original.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((ph, i) => (
            <div key={i} className="relative border rounded-xl overflow-hidden bg-slate-50" data-testid={`product-image-${i}`}>
              <img src={srcOf(ph)} alt="" className="w-full aspect-square object-contain bg-white" />
              {i === 0 && <span className="absolute top-1 left-1 text-[10px] font-black bg-brand-green text-white px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"><Star className="h-3 w-3" /> MAIN</span>}
              <button onClick={() => removePhoto(i)} data-testid={`product-image-remove-${i}`} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><Trash2 className="h-3.5 w-3.5" /></button>
              <div className="p-1.5 text-[11px] space-y-1">
                {ph.status === "cleaning" ? (
                  <div className="flex items-center gap-1 text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Cleaning…</div>
                ) : (
                  <>
                    {!ph.existing && (
                      <div className="flex gap-1">
                        <button onClick={() => setUse(i, "cleaned")} disabled={!ph.cleaned} data-testid={`product-image-use-cleaned-${i}`} className={`flex-1 rounded px-1 py-0.5 font-bold ${ph.use === "cleaned" && ph.cleaned ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-600"} disabled:opacity-40`}>Cleaned</button>
                        <button onClick={() => setUse(i, "original")} data-testid={`product-image-use-original-${i}`} className={`flex-1 rounded px-1 py-0.5 font-bold ${ph.use === "original" ? "bg-brand-navy text-white" : "bg-slate-100 text-slate-600"}`}>Original</button>
                      </div>
                    )}
                    {i !== 0 && (
                      <button onClick={() => makeMain(i)} data-testid={`product-image-make-main-${i}`} className="w-full rounded px-1 py-0.5 font-bold bg-brand-yellowLight text-brand-navy inline-flex items-center justify-center gap-1"><Star className="h-3 w-3" /> Make main</button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <div className="border-2 border-dashed border-slate-300 rounded-xl aspect-square flex flex-col items-center justify-center gap-1 p-2">
              <button onClick={() => fileRef.current?.click()} data-testid="product-image-add" className="w-full inline-flex items-center justify-center gap-1 text-slate-500 hover:text-brand-green text-xs font-bold py-1.5">
                <Plus className="h-4 w-4" /> Add photo
              </button>
              <div className="w-full border-t border-slate-200" />
              <button onClick={() => setCamOpen(true)} data-testid="product-image-camera" className="w-full inline-flex items-center justify-center gap-1 text-slate-500 hover:text-brand-navy text-xs font-bold py-1.5">
                <Camera className="h-4 w-4" /> Take photo
              </button>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="product-image-file-input" />
        {camOpen && <CameraCapture onCapture={(d) => { setCamOpen(false); addFromDataUrl(d); }} onClose={() => setCamOpen(false)} />}

        {photos.length === 0 && (
          <div className="text-center text-slate-400 text-sm py-4"><ImageIcon className="h-8 w-8 mx-auto mb-1" /> No photos yet.</div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={save} disabled={saving} data-testid="product-images-save" className="bg-brand-navy text-white rounded-full px-6 h-11 font-bold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Check className="h-4 w-4" /> Save images</>}
          </button>
          <button onClick={onClose} className="rounded-full px-4 h-11 font-bold border border-slate-200">Cancel</button>
        </div>
      </div>
    </div>
  );
};
